/**
 * Mapping-audit aggregate panel — Owner-only signal surfaced on /tenants.
 *
 * Calls /api/owner/mapping-audit/summary?days=7 and renders a single
 * "23 mapping inferences this week — review →" pulse plus a small table
 * of the top connectors by warning count. Pairs with DriftWatchesOverview
 * and LeadForwarderHealthPanel above it.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollText, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso); }
};

export function MappingAuditAggregatePanel({ defaultDays = 7 }) {
  const [days, setDays] = useState(defaultDays);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (d = days) => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/owner/mapping-audit/summary?days=${d}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const total = data?.warnings_total ?? 0;
  const top = data?.top_by_warnings || [];

  return (
    <section
      className="mt-6 rounded-2xl border border-black/5 bg-white p-6"
      data-testid="mapping-audit-aggregate-panel"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-ek-purple/10 p-2 text-ek-purple">
            <ScrollText size={18} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-ek-ink">
              Mapping audit · last {data?.window_days ?? days} days
            </h2>
            <p className="mt-0.5 text-sm text-ek-ink/65">
              Canonical-input contract inferences across all tenants. Click a
              row to inspect that connector&apos;s warnings.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-black/10 bg-white px-2 py-1 text-xs text-ek-ink"
            data-testid="mapping-audit-window-select"
            aria-label="Window in days"
          >
            <option value={1}>24h</option>
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
          </select>
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="mapping-audit-refresh"
            className="rounded-md border border-black/10 px-2.5 py-1 text-xs font-semibold text-ek-ink hover:border-ek-purple/40 hover:text-ek-purple disabled:opacity-50"
          >{loading ? "Refreshing…" : "Refresh"}</button>
        </div>
      </div>

      {err && <p className="mt-3 text-xs text-rose-600" data-testid="mapping-audit-error">{err}</p>}

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Inferences this period</p>
          <p
            className="mt-0.5 text-2xl font-semibold text-ek-ink"
            data-testid="mapping-audit-total"
          >{total.toLocaleString()}</p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Connectors active</p>
          <p className="mt-0.5 text-2xl font-semibold text-ek-ink">{(data?.connectors_in_window ?? 0).toLocaleString()}</p>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Tenants active</p>
          <p className="mt-0.5 text-2xl font-semibold text-ek-ink">{(data?.tenants_in_window ?? 0).toLocaleString()}</p>
        </div>
      </div>

      {top.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="mapping-audit-top-table">
            <thead>
              <tr className="text-left text-ek-ink/55">
                <th className="pb-2 font-medium">Tenant</th>
                <th className="pb-2 font-medium">Connector</th>
                <th className="pb-2 font-medium text-right">Warnings</th>
                <th className="pb-2 font-medium text-right">Records</th>
                <th className="pb-2 font-medium">Mapper</th>
                <th className="pb-2 font-medium">Last mapped</th>
                <th className="pb-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {top.map((r) => (
                <tr
                  key={r.connector_id}
                  className="border-t border-black/5"
                  data-testid={`mapping-audit-row-${r.connector_id}`}
                >
                  <td className="py-2 text-ek-ink">{r.tenant_name}</td>
                  <td className="py-2 text-ek-ink/80">{r.slug}</td>
                  <td className="py-2 text-right font-semibold text-amber-700">{r.warnings_count.toLocaleString()}</td>
                  <td className="py-2 text-right text-ek-ink/70">{r.records_synced.toLocaleString()}</td>
                  <td className="py-2 text-ek-ink/70">{r.mapper_version}</td>
                  <td className="py-2 text-ek-ink/70">{fmtDate(r.last_mapped_at)}</td>
                  <td className="py-2 text-right">
                    <Link
                      to={`/connectors`}
                      className="inline-flex items-center gap-0.5 text-ek-purple hover:underline"
                      data-testid={`mapping-audit-link-${r.connector_id}`}
                    >Review<ChevronRight size={12} /></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mt-3 text-xs text-ek-ink/55" data-testid="mapping-audit-empty">
          No mapping inferences raised in this window. Every record matched the canonical contract cleanly.
        </p>
      )}
    </section>
  );
}
